import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { CheckCircle2, PlayCircle, FileDown, LogOut, Sparkles, MessageCircle, Award, FileText } from "lucide-react";
import { toast } from "sonner";
import api, { auth } from "@/lib/api";
import { Progress } from "@/components/ui/progress";

const toEmbed = (url) => {
  const m = (url || "").match(/(?:youtu\.be\/|v=|embed\/)([\w-]{11})/);
  return m ? `https://www.youtube.com/embed/${m[1]}` : url;
};

export default function StudentDashboard() {
  const [course, setCourse] = useState(null);
  const [selected, setSelected] = useState(null);
  const nav = useNavigate();
  const user = auth.getUser();

  useEffect(() => {
    api.get("/student/course")
      .then((r) => {
        setCourse(r.data);
        const first = r.data.modules[0]?.lessons[0];
        if (first) setSelected({ ...first, moduleTitle: r.data.modules[0].title });
      })
      .catch(() => toast.error("No se pudo cargar el curso"));
  }, []);

  const toggleComplete = async (lesson) => {
    const done = course.completed_lessons.includes(lesson.id);
    try {
      const { data } = await api.post("/student/progress", { lesson_id: lesson.id, completed: !done });
      setCourse({ ...course, completed_lessons: data.completed_lessons });
      toast.success(done ? "Marcado como pendiente" : "¡Lección completada!");
    } catch { toast.error("Error"); }
  };

  const logout = () => { auth.logout(); nav("/"); };

  if (!course) return <div className="min-h-screen flex items-center justify-center">Cargando…</div>;

  const total = course.modules.reduce((s, m) => s + m.lessons.length, 0);
  const doneCount = course.completed_lessons.length;
  const pct = total > 0 ? Math.round((doneCount / total) * 100) : 0;

  return (
    <div data-testid="student-dashboard" className="min-h-screen bg-[var(--bg-main)]">
      <header className="bg-white border-b border-[var(--border-subtle)] sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Sparkles className="text-[var(--brand-primary)]" size={22}/>
            <span className="font-heading text-xl">Aula Valeriia</span>
          </div>
          <div className="flex items-center gap-4">
            <span className="text-sm text-[var(--text-muted)] hidden md:inline">{user?.email}</span>
            <button data-testid="logout-btn" onClick={logout} className="btn-outline-brand !py-2 !px-4 text-sm"><LogOut size={16}/> Salir</button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 py-8 grid lg:grid-cols-[320px_1fr] gap-6">
        {/* Sidebar */}
        <aside className="space-y-4">
          {total > 0 && (
            <div className="card-luxe p-5">
              <p className="text-xs uppercase text-[var(--text-muted)] tracking-widest mb-2">Tu progreso</p>
              <p className="font-heading text-3xl mb-2">{pct}%</p>
              <Progress value={pct} className="h-2"/>
              <p className="text-xs text-[var(--text-muted)] mt-2">{doneCount} / {total} lecciones completadas</p>
            </div>
          )}

          {course.has_mentor && (
            <div className="card-luxe p-5 bg-[var(--brand-primary-light)] border-[var(--brand-primary)]">
              <MessageCircle className="text-[var(--brand-primary)] mb-2" size={20}/>
              <h4 className="font-heading text-lg mb-1">Mentoría VIP activa</h4>
              <p className="text-xs text-[var(--text-muted)]">Escríbele a Valeriia para cualquier duda: <a href="mailto:valeriia@nailacademy.es" className="text-[var(--brand-primary)] underline">valeriia@nailacademy.es</a></p>
            </div>
          )}

          <div className="card-luxe p-5">
            <h4 className="font-heading text-lg mb-3 flex items-center gap-2"><FileDown size={18}/> Descargables PDF</h4>
            <ul className="space-y-2">
              {course.pdfs.map((p) => (
                <li key={p.id}>
                  <a data-testid={`pdf-${p.id}`} href={p.url} target="_blank" rel="noreferrer" className="flex items-center justify-between gap-2 text-sm p-2 rounded hover:bg-[var(--bg-alt)]">
                    <span className="flex-1">{p.title}</span>
                    <span className="text-xs text-[var(--text-muted)]">{p.size}</span>
                  </a>
                </li>
              ))}
            </ul>
          </div>
        </aside>

        {/* Content */}
        <section>
          {course.modules.length === 0 && (
            <div className="card-luxe p-8 text-center">
              <FileText className="mx-auto text-[var(--brand-primary)] mb-3" size={40}/>
              <h2 className="font-heading text-3xl mb-2">Tu Nails Book está listo</h2>
              <p className="text-[var(--text-muted)] mb-2">Descárgalo desde el panel «Descargables PDF».</p>
              <p className="text-sm text-[var(--text-muted)]">¿Quieres el curso completo en vídeo? Escríbenos por <a href="https://wa.me/message/AYCIE24LFTYEK1" target="_blank" rel="noreferrer" className="text-[var(--brand-primary)] underline">WhatsApp</a> y te contamos cómo ampliar tu acceso.</p>
            </div>
          )}

          {selected && (
            <div className="card-luxe overflow-hidden mb-6">
              {selected.youtube ? (
                <iframe
                  key={selected.id}
                  data-testid="video-player"
                  className="w-full aspect-video"
                  src={toEmbed(selected.youtube)}
                  title={selected.title}
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                />
              ) : (
                <video
                  key={selected.id}
                  data-testid="video-player"
                  controls
                  className="w-full aspect-video bg-black"
                  src={selected.custom_video
                    ? `${process.env.REACT_APP_BACKEND_URL}/api/lessons/${selected.id}/video?auth=${localStorage.getItem("token")}`
                    : selected.video_url}
                />
              )}
              <div className="p-6">
                <p className="text-xs text-[var(--brand-primary)] uppercase tracking-widest mb-1">{selected.moduleTitle}</p>
                <div className="flex items-start justify-between gap-4">
                  <h2 className="font-heading text-2xl md:text-3xl">{selected.title}</h2>
                  <button
                    data-testid={`complete-${selected.id}`}
                    onClick={() => toggleComplete(selected)}
                    className={course.completed_lessons.includes(selected.id) ? "btn-brand !py-2 !px-4 text-sm" : "btn-outline-brand !py-2 !px-4 text-sm"}
                  >
                    <CheckCircle2 size={16}/> {course.completed_lessons.includes(selected.id) ? "Completada" : "Marcar completada"}
                  </button>
                </div>
              </div>
            </div>
          )}

          <div className="space-y-4">
            {course.modules.map((m) => (
              <div key={m.id} className="card-luxe p-5">
                <div className="flex items-start justify-between mb-3">
                  <div>
                    <p className="text-xs text-[var(--brand-primary)] uppercase tracking-widest">Módulo {m.order}</p>
                    <h3 className="font-heading text-xl">{m.title}</h3>
                    <p className="text-sm text-[var(--text-muted)]">{m.description}</p>
                  </div>
                </div>
                <ul className="divide-y divide-[var(--border-subtle)]">
                  {m.lessons.map((l) => {
                    const done = course.completed_lessons.includes(l.id);
                    const isSelected = selected?.id === l.id;
                    return (
                      <li key={l.id}>
                        <button
                          data-testid={`lesson-${l.id}`}
                          onClick={() => setSelected({ ...l, moduleTitle: m.title })}
                          className={`w-full flex items-center gap-3 py-3 text-left hover:bg-[var(--bg-alt)] rounded px-2 ${isSelected ? "bg-[var(--brand-primary-light)]" : ""}`}
                        >
                          {done ? <CheckCircle2 size={18} className="text-[var(--brand-primary)]"/> : <PlayCircle size={18} className="text-[var(--text-muted)]"/>}
                          <span className="flex-1 text-sm">{l.title}</span>
                          <span className="text-xs text-[var(--text-muted)]">{l.duration}</span>
                        </button>
                      </li>
                    );
                  })}
                </ul>
              </div>
            ))}
          </div>

          {pct === 100 && (
            <div className="card-luxe p-8 mt-6 text-center bg-[var(--brand-primary-light)]">
              <Award className="mx-auto text-[var(--brand-gold)] mb-3" size={40}/>
              <h3 className="font-heading text-3xl mb-2">¡Enhorabuena!</h3>
              <p className="text-[var(--text-muted)]">Has completado el curso. Tu certificado digital estará disponible próximamente.</p>
            </div>
          )}
        </section>
      </main>
    </div>
  );
}
